import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, STATUSES, statusColor, statusLabel, priorityColor, priorityLabel, locationSuffix } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, ExternalLink, Pencil, Share2, Sparkles, FileText, Copy, Check, X, Clock, NotebookPen, Save, BellOff, Bell, Archive, Trash2, Download } from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { useSettings } from "../context/SettingsContext";
import { printDoc, downloadText, docSlug } from "../lib/docExport";

const ONE_DAY_MS = 86400000;
const COPY_RESET_MS = 1500;

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiMode, setAiMode] = useState("tailor");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiText, setAiText] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [archiveChecked, setArchiveChecked] = useState(false);
  const [prep, setPrep] = useState("");
  const [prepSaving, setPrepSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/jobs/${id}`);
      setJob(r.data);
      setPrep(r.data.interview_prep || "");
    } catch {
      toast.error("Job not found");
      navigate("/jobs");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const savePrep = async () => {
    setPrepSaving(true);
    try {
      await api.patch(`/jobs/${id}`, { interview_prep: prep });
      toast.success("Interview notes saved");
    } catch {
      toast.error("Could not save notes");
    } finally {
      setPrepSaving(false);
    }
  };

  const setSnooze = async (until) => {
    try {
      await api.patch(`/jobs/${id}`, { snoozed_until: until });
      toast.success(until ? "Reminders snoozed" : "Snooze cleared");
      load();
    } catch {
      toast.error("Could not update snooze");
    }
  };
  const snoozeDays = (d) => {
    const dt = new Date();
    dt.setDate(dt.getDate() + d);
    setSnooze(dt.toISOString());
  };

  const setStatus = async (status) => {
    const old = job.status;
    setJob({ ...job, status });
    try {
      await api.patch(`/jobs/${id}`, { status });
      toast.success(`Moved to ${statusLabel(status)}`);
      if (status === "rejected" && old !== "rejected") {
        if (settings.auto_archive_on_rejection) {
          await api.patch(`/jobs/${id}`, { archived: true });
          toast.success("Archived");
          navigate("/jobs");
        } else {
          setArchiveChecked(false);
          setRejectOpen(true);
        }
      } else {
        load();
      }
    } catch {
      setJob({ ...job, status: old });
      toast.error("Update failed");
    }
  };

  const confirmRejection = async () => {
    setRejectOpen(false);
    if (archiveChecked) {
      try {
        await api.patch(`/jobs/${id}`, { archived: true });
        toast.success("Archived");
        navigate("/jobs");
      } catch { toast.error("Archive failed"); }
    } else {
      load();
    }
  };

  const share = async () => {
    try {
      const r = await api.post(`/jobs/${id}/share`);
      const url = `${window.location.origin}/share/${r.data.share_id}`;
      setShareUrl(url);
      setShareOpen(true);
    } catch { toast.error("Could not generate link"); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), COPY_RESET_MS);
  };

  const runAI = async (mode) => {
    setAiMode(mode);
    setAiOpen(true);
    setAiLoading(true);
    setAiText("");
    try {
      if (mode === "tailor") {
        const r = await api.post("/ai/tailor-resume", { job_id: id });
        setAiText(stripFence(r.data.tailored));
      } else {
        const r = await api.post("/ai/cover-letter", { job_id: id });
        setAiText(stripFence(r.data.cover_letter));
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "AI failed";
      if (e.response?.status === 429) toast.warning(msg);
      setAiText(`Error: ${msg}`);
    } finally {
      setAiLoading(false);
    }
  };

  const copyAI = async () => {
    await navigator.clipboard.writeText(aiText);
    toast.success("Copied to clipboard");
  };

  const aiDocTitle = aiMode === "tailor"
    ? `Tailored Resume — ${job?.title || ""} @ ${job?.company || ""}`
    : `Cover Letter — ${job?.title || ""} @ ${job?.company || ""}`;
  const aiFileBase = docSlug(aiMode === "tailor" ? "resume" : "cover-letter", job?.company, job?.title);

  const downloadAiPdf = () => {
    printDoc(aiDocTitle, aiText, () => toast.error("Could not open the print dialog"));
  };

  const downloadAiTxt = () => {
    downloadText(`${aiFileBase}.txt`, aiText);
    toast.success("Downloaded .txt");
  };

  const [savingAi, setSavingAi] = useState(false);
  const [versionLabel, setVersionLabel] = useState("");
  const [editingVersionId, setEditingVersionId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const versions = (job?.ai_versions || []).slice().reverse(); // newest first

  const versionMeta = (v) =>
    v.kind === "resume"
      ? { label: "Tailored resume", title: `Tailored Resume — ${job?.title || ""} @ ${job?.company || ""}`, file: docSlug("resume", job?.company, job?.title) }
      : { label: "Cover letter", title: `Cover Letter — ${job?.title || ""} @ ${job?.company || ""}`, file: docSlug("cover-letter", job?.company, job?.title) };

  const saveAiVersion = async () => {
    setSavingAi(true);
    try {
      const kind = aiMode === "tailor" ? "resume" : "cover_letter";
      const r = await api.post(`/jobs/${id}/ai-versions`, { kind, content: aiText, label: versionLabel.trim() || undefined });
      setJob(r.data);
      setVersionLabel("");
      toast.success("Saved to this job");
    } catch {
      toast.error("Could not save");
    } finally {
      setSavingAi(false);
    }
  };

  const openSavedVersion = (v) => {
    setAiMode(v.kind === "resume" ? "tailor" : "cover");
    setAiText(v.content);
    setAiLoading(false);
    setAiOpen(true);
  };

  const downloadVersion = (v, kind) => {
    const meta = versionMeta(v);
    if (kind === "pdf") printDoc(meta.title, v.content, () => toast.error("Could not open the print dialog"));
    else { downloadText(`${meta.file}.txt`, v.content); toast.success("Downloaded .txt"); }
  };

  const deleteVersion = async (versionId) => {
    try {
      const r = await api.delete(`/jobs/${id}/ai-versions/${versionId}`);
      setJob(r.data);
      toast.success("Version deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const startRename = (v) => { setEditingVersionId(v.id); setEditLabel(v.label || ""); };
  const cancelRename = () => { setEditingVersionId(null); setEditLabel(""); };
  const saveRename = async (versionId) => {
    try {
      const r = await api.patch(`/jobs/${id}/ai-versions/${versionId}`, { label: editLabel.trim() || null });
      setJob(r.data);
      cancelRename();
      toast.success("Renamed");
    } catch {
      toast.error("Rename failed");
    }
  };

  const toggleArchive = async () => {
    try {
      await api.patch(`/jobs/${id}`, { archived: !job.archived });
      toast.success(job.archived ? "Restored to active" : "Archived");
      navigate("/jobs");
    } catch {
      toast.error("Could not update");
    }
  };

  const deleteJob = async () => {
    try {
      await api.delete(`/jobs/${id}`);
      toast.success("Deleted");
      navigate("/jobs");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) return <div className="label-tiny">Loading…</div>;
  if (!job) return null;

  return (
    <div className="max-w-4xl mx-auto" data-testid="job-detail-page">
      <button onClick={() => navigate("/jobs")} className="label-tiny inline-flex items-center mb-4 hover:text-zinc-900" data-testid="detail-back">
        <ChevronLeft size={14} className="mr-1" /> Back to jobs
      </button>

      {/* Header */}
      <div className="brutal-card p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="label-tiny" data-testid="detail-company">{job.company}{locationSuffix(job)}</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2" data-testid="detail-title">{job.title}</h1>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {job.salary_display && <div className="font-mono-jb text-sm text-zinc-600" data-testid="detail-salary">{job.salary_display}</div>}
              <span className="inline-flex items-center text-xs font-mono-jb uppercase tracking-wider text-zinc-600" data-testid="detail-priority">
                <span className="status-dot" style={{ backgroundColor: priorityColor(job.priority) }} />
                {priorityLabel(job.priority)} priority
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {job.url && (
              <a href={job.url} target="_blank" rel="noopener noreferrer" data-testid="detail-apply-link">
                <Button variant="outline" className="rounded-none h-10 border-zinc-300">
                  Apply <ExternalLink size={14} className="ml-1.5" />
                </Button>
              </a>
            )}
            <Link to={`/jobs/${id}/edit`}>
              <Button variant="outline" className="rounded-none h-10 border-zinc-300" data-testid="detail-edit-btn">
                <Pencil size={14} className="mr-1.5" /> Edit
              </Button>
            </Link>
            <Button variant="outline" onClick={share} className="rounded-none h-10 border-zinc-300" data-testid="detail-share-btn">
              <Share2 size={14} className="mr-1.5" /> Share
            </Button>
            <Button variant="outline" onClick={toggleArchive} className="rounded-none h-10 border-zinc-300" data-testid="detail-archive-btn">
              <Archive size={14} className="mr-1.5" /> {job.archived ? "Unarchive" : "Archive"}
            </Button>
            <Button variant="outline" onClick={() => setDeleteOpen(true)} className="rounded-none h-10 border-zinc-300 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="detail-delete-btn">
              <Trash2 size={14} className="mr-1.5" /> Delete
            </Button>
          </div>
        </div>

        {/* Status row */}
        <div className="mt-6 pt-6 border-t border-zinc-200 flex items-center gap-3 flex-wrap">
          <span className="label-tiny">Status</span>
          <Select value={job.status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-none h-10 w-48" data-testid="detail-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s.key} value={s.key} data-testid={`detail-status-${s.key}`}>
                  <span className="flex items-center">
                    <span className="status-dot" style={{ backgroundColor: s.color }} />
                    {s.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="flex items-center text-xs ml-1">
            <span className="status-dot" style={{ backgroundColor: statusColor(job.status) }} />
            <span className="font-mono-jb uppercase tracking-wider">{job.status}</span>
          </span>

          <div className="ml-auto flex items-center gap-2">
            {isSnoozed(job.snoozed_until) && (
              <span className="label-tiny text-amber-700 flex items-center gap-1" data-testid="snooze-badge">
                <BellOff size={12} /> Snoozed to {fmtShort(job.snoozed_until)}
              </span>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="rounded-none h-10 border-zinc-300" data-testid="snooze-trigger">
                  {isSnoozed(job.snoozed_until) ? <BellOff size={14} className="mr-1.5" /> : <Bell size={14} className="mr-1.5" />}
                  Snooze
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="rounded-none w-56 p-2 border-zinc-200" data-testid="snooze-panel">
                <div className="label-tiny px-2 py-1.5">Pause reminders for</div>
                <button onClick={() => snoozeDays(7)} data-testid="snooze-7" className="w-full text-left px-2 py-2 text-sm hover:bg-zinc-100">7 days</button>
                <button onClick={() => snoozeDays(30)} data-testid="snooze-30" className="w-full text-left px-2 py-2 text-sm hover:bg-zinc-100">30 days</button>
                <div className="px-2 py-2 border-t border-zinc-100 mt-1">
                  <div className="label-tiny mb-1.5">Custom date</div>
                  <input
                    type="date"
                    min={new Date(Date.now() + ONE_DAY_MS).toISOString().slice(0, 10)}
                    onChange={(e) => e.target.value && setSnooze(new Date(e.target.value).toISOString())}
                    data-testid="snooze-custom-date"
                    className="w-full border border-zinc-300 h-9 px-2 text-sm rounded-none"
                  />
                </div>
                {isSnoozed(job.snoozed_until) && (
                  <button onClick={() => setSnooze("")} data-testid="snooze-clear" className="w-full text-left px-2 py-2 text-sm text-red-600 hover:bg-red-50 border-t border-zinc-100 mt-1">
                    Clear snooze
                  </button>
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {/* AI tools */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
        <button onClick={() => runAI("tailor")} data-testid="ai-tailor-btn" className="brutal-card p-5 text-left transition-colors">
          <Sparkles size={18} strokeWidth={1.5} />
          <h3 className="font-heading font-semibold mt-3">Tailor my resume</h3>
          <p className="text-sm text-zinc-600 mt-1">Rewrite your resume for this role using AI.</p>
        </button>
        <button onClick={() => runAI("cover")} data-testid="ai-cover-btn" className="brutal-card p-5 text-left transition-colors">
          <FileText size={18} strokeWidth={1.5} />
          <h3 className="font-heading font-semibold mt-3">Generate cover letter</h3>
          <p className="text-sm text-zinc-600 mt-1">Three-paragraph cover letter tailored to this job.</p>
        </button>
      </div>

      {/* Saved AI versions */}
      {versions.length > 0 && (
        <div className="mt-6 brutal-card p-6" data-testid="saved-versions">
          <div className="label-tiny mb-4">Saved AI versions · {versions.length}</div>
          <div className="border border-zinc-200">
            {versions.map((v) => {
              const meta = versionMeta(v);
              return (
                <div key={v.id} className="flex items-center justify-between gap-3 p-3 border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50" data-testid={`saved-version-${v.id}`}>
                  {editingVersionId === v.id ? (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Input
                        autoFocus
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(v.id); if (e.key === "Escape") cancelRename(); }}
                        placeholder={meta.label}
                        maxLength={60}
                        className="rounded-none border-zinc-300 h-8"
                        data-testid={`saved-version-rename-input-${v.id}`}
                      />
                      <Button onClick={() => saveRename(v.id)} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white h-8 px-2.5 shrink-0" data-testid={`saved-version-rename-save-${v.id}`}>
                        <Check size={13} />
                      </Button>
                      <Button variant="outline" onClick={cancelRename} className="rounded-none border-zinc-300 h-8 px-2.5 shrink-0" data-testid={`saved-version-rename-cancel-${v.id}`}>
                        <X size={13} />
                      </Button>
                    </div>
                  ) : (
                    <button onClick={() => openSavedVersion(v)} className="min-w-0 flex-1 text-left" data-testid={`saved-version-open-${v.id}`}>
                      <div className="flex items-center gap-2">
                        {v.kind === "resume" ? <Sparkles size={14} strokeWidth={1.5} /> : <FileText size={14} strokeWidth={1.5} />}
                        <span className="font-medium text-sm truncate">{v.label || meta.label}</span>
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5">{v.label ? `${meta.label} · ` : ""}{fmtDate(v.created_at)}</div>
                    </button>
                  )}
                  {editingVersionId !== v.id && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button variant="ghost" onClick={() => startRename(v)} className="rounded-none h-8 px-2 text-zinc-500 hover:text-zinc-900" data-testid={`saved-version-rename-${v.id}`}>
                        <Pencil size={13} />
                      </Button>
                      <Button variant="outline" onClick={() => downloadVersion(v, "txt")} className="rounded-none border-zinc-300 h-8 px-2.5" data-testid={`saved-version-txt-${v.id}`}>
                        <Download size={13} className="mr-1" /> .txt
                      </Button>
                      <Button variant="outline" onClick={() => downloadVersion(v, "pdf")} className="rounded-none border-zinc-300 h-8 px-2.5" data-testid={`saved-version-pdf-${v.id}`}>
                        <FileText size={13} className="mr-1" /> PDF
                      </Button>
                      <Button variant="ghost" onClick={() => deleteVersion(v.id)} className="rounded-none h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`saved-version-delete-${v.id}`}>
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Description + notes */}
      {(job.description || job.notes) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
          {job.description && (
            <div className="brutal-card p-6">
              <div className="label-tiny mb-3">Description</div>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-800">{job.description}</p>
            </div>
          )}
          {job.notes && (
            <div className="brutal-card p-6">
              <div className="label-tiny mb-3">Notes (private)</div>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-800">{job.notes}</p>
            </div>
          )}
        </div>
      )}

      {/* Interview prep + Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <div className="brutal-card p-6" data-testid="interview-prep-card">
          <div className="flex items-center justify-between mb-3">
            <div className="label-tiny flex items-center gap-2"><NotebookPen size={13} /> Interview prep</div>
            <Button onClick={savePrep} disabled={prepSaving} className="rounded-none h-8 px-3 bg-zinc-900 hover:bg-zinc-800 text-white text-xs" data-testid="prep-save-btn">
              <Save size={12} className="mr-1" /> {prepSaving ? "Saving…" : "Save"}
            </Button>
          </div>
          <Textarea
            data-testid="prep-textarea"
            value={prep}
            onChange={(e) => setPrep(e.target.value)}
            rows={7}
            placeholder="Questions to ask · talking points · people you're meeting · STAR stories to rehearse…"
            className="rounded-none text-sm"
          />
        </div>

        <div className="brutal-card p-6" data-testid="status-timeline">
          <div className="label-tiny flex items-center gap-2 mb-4"><Clock size={13} /> Application timeline</div>
          {(job.status_history && job.status_history.length > 0) ? (
            <ol className="space-y-3">
              {[...job.status_history].reverse().map((h, i) => (
                <li key={`${h.at}-${i}`} className="flex items-start gap-3" data-testid={`timeline-item-${i}`}>
                  <span className="status-dot mt-1.5" style={{ backgroundColor: statusColor(h.status) }} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{statusLabel(h.status)}</div>
                    <div className="font-mono-jb text-xs text-zinc-500">{fmtDate(h.at)}</div>
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-sm text-zinc-500">No status changes recorded yet.</p>
          )}
        </div>
      </div>

      {/* AI dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="rounded-none max-w-3xl" data-testid="ai-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {aiMode === "tailor" ? "Tailored Resume" : "Cover Letter"}
            </DialogTitle>
          </DialogHeader>
          {aiLoading ? (
            <div className="space-y-2 py-4">
              {[...Array(8)].map((_, i) => <div key={`sk-${i}`} className="h-3 bg-zinc-100" style={{ width: `${60 + (i*4)%40}%` }} />)}
              <div className="label-tiny pt-4">Claude is thinking…</div>
            </div>
          ) : (
            <>
              <div className="border border-zinc-200 p-4 max-h-[60vh] overflow-auto bg-zinc-50">
                <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed">{aiText}</pre>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2 mt-3">
                {!aiText.startsWith("Error:") && (
                  <>
                    <Input
                      value={versionLabel}
                      onChange={(e) => setVersionLabel(e.target.value)}
                      placeholder="Label (e.g. v2 – more metrics)"
                      maxLength={60}
                      className="rounded-none border-zinc-300 h-9 mr-auto w-full sm:w-56"
                      data-testid="ai-version-label-input"
                    />
                    <Button variant="outline" onClick={saveAiVersion} disabled={savingAi} className="rounded-none border-zinc-300" data-testid="ai-save-version-btn">
                      <Save size={14} className="mr-1.5" /> {savingAi ? "Saving…" : "Save to this job"}
                    </Button>
                    <Button variant="outline" onClick={downloadAiTxt} className="rounded-none border-zinc-300" data-testid="ai-download-txt-btn">
                      <Download size={14} className="mr-1.5" /> .txt
                    </Button>
                    <Button variant="outline" onClick={downloadAiPdf} className="rounded-none border-zinc-300" data-testid="ai-download-pdf-btn">
                      <FileText size={14} className="mr-1.5" /> PDF
                    </Button>
                  </>
                )}
                <Button onClick={copyAI} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="ai-copy-btn">
                  <Copy size={14} className="mr-1.5" /> Copy
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Share this lead</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600">Anyone with this link can view the job details (read-only).</p>
          <div className="flex items-stretch border border-zinc-200">
            <input data-testid="share-url-input" readOnly value={shareUrl} className="flex-1 px-3 text-sm bg-zinc-50 font-mono-jb truncate" />
            <button onClick={copy} className="bg-zinc-900 text-white px-4" data-testid="share-copy-btn">
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Rejection → archive dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Move to archive?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600">This job is now marked rejected. You can stash it in the archive to keep your active list clean.</p>
          <label className="flex items-center gap-3 mt-2 cursor-pointer">
            <Checkbox checked={archiveChecked} onCheckedChange={(v) => setArchiveChecked(!!v)} data-testid="reject-archive-checkbox" className="rounded-none" />
            <span className="text-sm">Move to archive</span>
          </label>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => { setRejectOpen(false); load(); }} className="rounded-none h-10 border-zinc-300" data-testid="reject-cancel">Keep here</Button>
            <Button onClick={confirmRejection} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white h-10" data-testid="reject-confirm">Confirm</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Delete this job?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600">This permanently removes <span className="font-semibold">{job.title}</span> at {job.company}. This can't be undone.</p>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setDeleteOpen(false)} className="rounded-none h-10 border-zinc-300" data-testid="delete-cancel">Cancel</Button>
            <Button onClick={deleteJob} className="rounded-none bg-red-600 hover:bg-red-700 text-white h-10" data-testid="delete-confirm">Delete forever</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Strip a wrapping ```lang ... ``` code fence the LLM sometimes adds around output.
const stripFence = (s = "") => {
  const t = String(s).trim();
  const m = t.match(/^```[a-zA-Z]*\n([\s\S]*?)\n```$/);
  return m ? m[1].trim() : t;
};

const fmtDate = (iso) => {  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const isSnoozed = (iso) => {
  if (!iso) return false;
  return new Date(iso).getTime() > Date.now();
};

const fmtShort = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};
